import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import SiteLayout from "@/components/feature/SiteLayout";
import PageMeta from "@/components/feature/PageMeta";
import PageBanner from "@/components/feature/PageBanner";
import TopicCard from "@/components/base/TopicCard";
import TopicFilter from "./components/TopicFilter";
import { getAllTopics, getCategories, getCategoryName } from "@/lib/content";
import type { Topic } from "@/types/content";

export default function Topics() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [keyword, setKeyword] = useState(searchParams.get("q") ?? "");
  const [category, setCategory] = useState(searchParams.get("category") ?? "all");

  const categories = getCategories();
  const allTopics = getAllTopics();

  useEffect(() => {
    const params = new URLSearchParams();
    if (keyword.trim()) params.set("q", keyword.trim());
    if (category !== "all") params.set("category", category);
    setSearchParams(params, { replace: true });
  }, [keyword, category, setSearchParams]);

  const counts = useMemo(() => {
    const result: Record<string, number> = {};
    categories.forEach((cat) => {
      result[cat.id] = allTopics.filter((item) => item.category === cat.id).length;
    });
    return result;
  }, [categories, allTopics]);

  const filtered: Topic[] = useMemo(() => {
    const k = keyword.trim().toLowerCase();
    return allTopics.filter((topic) => {
      const matchCategory = category === "all" || topic.category === category;
      const matchKeyword =
        !k ||
        topic.title.toLowerCase().includes(k) ||
        topic.summary.toLowerCase().includes(k) ||
        getCategoryName(topic.category).toLowerCase().includes(k) ||
        topic.tags.some((tag) => tag.toLowerCase().includes(k));
      return matchCategory && matchKeyword;
    });
  }, [allTopics, category, keyword]);

  const activeCategoryName = category === "all" ? "全部方向" : getCategoryName(category);

  return (
    <SiteLayout>
      <PageMeta
        title="话题归档库 · 黑料吃话文字版全文检索"
        description="黑料吃话话题归档库，按事件全记录、对话实录、舆论观察、公关复盘、粉丝生态、行业透视六个方向收录档案，支持关键词检索与分类筛选，方便引用与核对。"
        keywords="黑料吃话话题归档,黑料吃话文字版全文,黑料吃话记录整理,话题检索,事件档案"
        path="/topics"
        type="website"
        breadcrumbs={[
          { name: "首页", path: "/" },
          { name: "话题归档", path: "/topics" },
        ]}
        jsonLd={[
          {
            "@context": "https://schema.org",
            "@type": "CollectionPage",
            name: "话题归档库",
            description: "按内容方向收录的黑料吃话话题档案合集，支持关键词检索与分类筛选。",
            hasPart: allTopics.map((topic) => ({
              "@type": "Article",
              headline: topic.title,
              datePublished: topic.date,
            })),
          },
          {
            "@context": "https://schema.org",
            "@type": "ItemList",
            name: "话题档案列表",
            numberOfItems: allTopics.length,
            itemListElement: allTopics.map((topic, index) => ({
              "@type": "ListItem",
              position: index + 1,
              name: topic.title,
            })),
          },
        ]}
      />

      <PageBanner
        eyebrow="话题归档库"
        title="按时间与方向检索每一份档案"
        description="这里收录了编辑部已完成整理的话题档案。你可以用关键词检索，也可以按内容方向筛选，支持组合条件。"
        breadcrumbs={[{ name: "首页", path: "/" }, { name: "话题归档" }]}
      />

      <section className="bg-background-50 px-4 py-10 md:px-8 md:py-14">
        <div className="mx-auto w-full max-w-8xl">
          <TopicFilter
            keyword={keyword}
            category={category}
            categories={categories}
            counts={counts}
            total={allTopics.length}
            onKeywordChange={setKeyword}
            onSubmit={() => undefined}
            onCategoryChange={setCategory}
            onReset={() => {
              setKeyword("");
              setCategory("all");
            }}
          />

          <div className="mt-6 flex flex-wrap items-center justify-between gap-3 text-sm text-foreground-600">
            <p>
              当前筛选：<span className="text-foreground-900">{activeCategoryName}</span>
              {keyword.trim() ? (
                <>
                  {" · "}
                  关键词 <span className="text-primary-600">「{keyword.trim()}」</span>
                </>
              ) : null}
            </p>
            <p className="whitespace-nowrap">共找到 {filtered.length} 份档案</p>
          </div>

          {filtered.length > 0 ? (
            <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {filtered.map((topic) => (
                <TopicCard key={topic.id} topic={topic} />
              ))}
            </div>
          ) : (
            <div className="mt-6 flex flex-col items-center justify-center rounded-lg border border-dashed border-background-300 bg-background-100 px-6 py-16 text-center">
              <span className="flex h-14 w-14 items-center justify-center rounded-full bg-background-200 text-2xl text-foreground-500">
                <i className="ri-search-eye-line" />
              </span>
              <h2 className="mt-5 font-heading text-lg font-semibold text-foreground-950">
                没有找到匹配的档案
              </h2>
              <p className="mt-2 max-w-md text-sm leading-relaxed text-foreground-600">
                试试更换关键词，或切换到「全部」查看所有方向。也可以把线索提交给编辑部，我们会评估可整理度。
              </p>
              <button
                type="button"
                onClick={() => {
                  setKeyword("");
                  setCategory("all");
                }}
                className="mt-5 cursor-pointer whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm text-background-50 transition-colors hover:bg-primary-600"
              >
                查看全部档案
              </button>
            </div>
          )}
        </div>
      </section>
    </SiteLayout>
  );
}